import {
  CloudUploadOutlined,
  DownOutlined,
  FileDoneOutlined,
  FileTextOutlined
} from "@ant-design/icons"
import { Button, Dropdown, Typography } from "antd"
import React, { Suspense, lazy, useState } from "react"
import { useTranslation } from "react-i18next"

import { uploadContract } from "../../../../Contracts/redux/contracts.thunks.ts"
import { TEMPLATE_DOCUMENT_TYPES } from "../../../../DocumentTemplates/constants/template-document-type.tsx"
import type {
  LeadContract,
  LeadProposal
} from "../../../types/lead.documents.types.ts"
import { DocumentCreateMenuOption } from "../../../LeadWorkspacePage/documents/DocumentCreateMenuOption.tsx"
import { LeadViewDocumentUpload } from "../LeadViewDocument/LeadViewDocumentUpload.tsx"

const LeadViewTemplatesModal = lazy(
  () => import("../LeadViewTemplatesModal/LeadViewTemplatesModal.tsx")
)

const { Text } = Typography

interface LeadViewContractsCreateTriggerProps {
  leadId: string
  proposals?: LeadProposal[] | null
  /** From-proposal and from-template options only apply on stages where a contract can be created. */
  canCreateContract: boolean
  /** The upload option applies on its own, wider set of stages (e.g. also once Won). */
  canUploadContract: boolean
}

/**
 * All three ways to add a contract — from an existing proposal, from a
 * contract template, or an upload — unified under one "Create" dropdown
 * instead of split across this trigger and a separate top-level Upload
 * button. "From Existing Proposal" and "From Contract Template" both open
 * the same `LeadViewTemplatesModal`, but as two genuinely different flows,
 * not one flow with two doors in: "From Existing Proposal" runs
 * `proposalFirst`, which is Proposal selection ONLY — confirming it creates
 * the Contract directly from that Proposal's own blocks/structured data,
 * with no Contract Template step and no `template_id` at all. "From Contract
 * Template" is the other, unaffected flow: template first, then optionally a
 * Proposal, always ending in a `template_id` — without one there that
 * page's create call falls back to the backend's approved-proposals-only
 * path, which is what broke "From Existing Proposal" when it used to route
 * through that same page. Upload runs its own thunk, unrelated to either.
 * The two stage sets diverge (upload stays available on Won, creation does
 * not), so each option is gated individually rather than the whole trigger.
 */
export function LeadViewContractsCreateTrigger({
  leadId,
  proposals,
  canCreateContract,
  canUploadContract
}: LeadViewContractsCreateTriggerProps) {
  const { t } = useTranslation()
  const [showTemplatesModal, setShowTemplatesModal] = useState(false)
  const [templatesModalProposalFirst, setTemplatesModalProposalFirst] =
    useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  const openTemplatesModal = (proposalFirst = false) => {
    setTemplatesModalProposalFirst(proposalFirst)
    setShowTemplatesModal(true)
  }
  const closeTemplatesModal = () => setShowTemplatesModal(false)

  const hasProposals = !!proposals?.length

  return (
    <>
      {showTemplatesModal && (
        <Suspense>
          <LeadViewTemplatesModal
            leadId={leadId}
            type={TEMPLATE_DOCUMENT_TYPES.CONTRACT}
            proposals={proposals}
            proposalFirst={templatesModalProposalFirst}
            onClose={closeTemplatesModal}
          />
        </Suspense>
      )}

      <Dropdown
        trigger={["click"]}
        open={menuOpen}
        onOpenChange={setMenuOpen}
        popupRender={() => (
          <div
            className={
              "w-80 rounded-card border border-border bg-white p-2 shadow-lg"
            }
          >
            <Text
              type={"secondary"}
              className={"text-xs px-2 block mb-1"}
            >
              {t("leadWorkspace.cards.documents.createContractMenuTitle")}
            </Text>
            {canCreateContract && (
              <>
                <DocumentCreateMenuOption
                  icon={<FileTextOutlined />}
                  accent={"indigo"}
                  title={t(
                    "leadWorkspace.cards.documents.fromExistingProposal"
                  )}
                  description={t(
                    "leadWorkspace.cards.documents.fromExistingProposalDescription"
                  )}
                  disabled={!hasProposals}
                  onClick={() => {
                    setMenuOpen(false)
                    openTemplatesModal(true)
                  }}
                />
                <DocumentCreateMenuOption
                  icon={<FileDoneOutlined />}
                  accent={"green"}
                  title={t(
                    "leadWorkspace.cards.documents.fromContractTemplate"
                  )}
                  description={t(
                    "leadWorkspace.cards.documents.fromContractTemplateDescription"
                  )}
                  onClick={() => {
                    setMenuOpen(false)
                    openTemplatesModal()
                  }}
                />
              </>
            )}
            {canUploadContract && (
              <LeadViewDocumentUpload<LeadContract>
                leadId={leadId}
                documentType={"contract"}
                uploadThunk={uploadContract}
                render={() => (
                  <DocumentCreateMenuOption
                    icon={<CloudUploadOutlined />}
                    accent={"orange"}
                    title={t(
                      "leadWorkspace.cards.documents.uploadContractOption"
                    )}
                    description={t(
                      "leadWorkspace.cards.documents.uploadContractOptionDescription"
                    )}
                  />
                )}
              />
            )}
          </div>
        )}
      >
        <Button type={"primary"}>
          {t("leadWorkspace.cards.documents.newContract")}
          <DownOutlined className={"text-xs"} />
        </Button>
      </Dropdown>
    </>
  )
}
